import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BlogPublicLayout from "../../components/blog/BlogPublicLayout";
import { BLOG_POSTS } from "../../config/blogPosts";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.45 },
};

const BlogIndex = () => {
  return (
    <BlogPublicLayout>
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 px-4 py-14 text-white sm:px-6 sm:py-20">
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />
        <motion.div
          className="relative mx-auto max-w-6xl text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-orange-100">
            TabTrips Extranet
          </p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Partner blog
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-orange-50/95 sm:text-lg">
            Guides, launch offers, and tips to help hotel owners grow bookings, visibility,
            and revenue on TabTrips.
          </p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <motion.div {...fadeUp}>
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Latest articles</h2>
          <p className="mt-2 text-gray-600">
            Insights for property owners, resorts, homestays, and partner managers.
          </p>
        </motion.div>

        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((post, index) => (
            <motion.article
              key={post.slug}
              {...fadeUp}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-lg"
            >
              <Link
                to={`/blog/${post.slug}`}
                className="relative block aspect-[16/10] overflow-hidden bg-gray-100 no-underline"
              >
                {post.image ? (
                  <img
                    src={post.image}
                    alt=""
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${post.cardGradient || "from-orange-500 to-amber-500"} p-6`}
                  >
                    <span className="text-center text-lg font-bold text-white">{post.tag}</span>
                  </div>
                )}
                <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-orange-700 shadow-sm">
                  {post.tag}
                </span>
              </Link>

              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <time dateTime="2026-05">{post.date}</time>
                  <span aria-hidden>·</span>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="text-lg font-bold leading-snug text-gray-900 group-hover:text-orange-700">
                  <Link to={`/blog/${post.slug}`} className="no-underline text-inherit">
                    {post.title}
                  </Link>
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-4 inline-flex items-center text-sm font-semibold text-orange-600 no-underline hover:text-orange-700"
                >
                  Read article
                  <span className="ml-1 transition group-hover:translate-x-0.5" aria-hidden>
                    →
                  </span>
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        {BLOG_POSTS.length === 0 && (
          <p className="mt-8 text-center text-gray-500">No articles published yet.</p>
        )}
      </section>
    </BlogPublicLayout>
  );
};

export default BlogIndex;
